import { Vector2 } from "../../../maths/Vector2";
import { Stat } from "../../../utils/Stat";
import { ShaderDefines2D } from "../../../webgl/shader/d2/ShaderDefines2D";
import { WebDefineDatas } from "../../RenderModuleData/WebModuleData/WebDefineDatas";
import { WebGLEngine } from "../RenderDevice/WebGLEngine";
export class Pass2DCacheInfo {
    constructor() {
        this.shaderInss = [];
        this.cacheFlagX = -1;
        this.cacheFlagY = -1;
        this.cachedDirtyVersion = -1;
        this.passDefineFlag = new Vector2(-1, -1);
    }
}
export class WebGLRenderElement2D {
    constructor() {
        this.renderStateIsBySprite = true;
        this.type = 0;
        this._defineChangeFlag = new Vector2(-1, -1);
        this._dirtyVersion = 0;
        this._passCache = new Map();
    }
    get subShader() { return this._subShader; }
    set subShader(value) {
        if (this._subShader !== value) {
            this._subShader = value;
            this._dirtyVersion++;
        }
    }
    get materialShaderData() { return this._materialShaderData; }
    set materialShaderData(value) {
        if (this._materialShaderData !== value) {
            this._unregisterDefineFlag(this._materialShaderData);
            this._materialShaderData = value;
            this._registerDefineFlag(value);
            this._dirtyVersion++;
        }
    }
    get value2DShaderData() { return this._value2DShaderData; }
    set value2DShaderData(value) {
        if (this._value2DShaderData !== value) {
            this._unregisterDefineFlag(this._value2DShaderData);
            this._value2DShaderData = value;
            this._registerDefineFlag(value);
            this._dirtyVersion++;
        }
    }
    get globalShaderData() { return this._globalShaderData; }
    set globalShaderData(value) {
        if (this._globalShaderData !== value) {
            this._unregisterDefineFlag(this._globalShaderData);
            this._globalShaderData = value;
            this._registerDefineFlag(value);
            this._dirtyVersion++;
        }
    }
    _registerDefineFlag(data) {
        var _a;
        (_a = data === null || data === void 0 ? void 0 : data._defineDatas) === null || _a === void 0 ? void 0 : _a.addChangeFlagInfo(this._defineChangeFlag);
    }
    _unregisterDefineFlag(data) {
        var _a;
        (_a = data === null || data === void 0 ? void 0 : data._defineDatas) === null || _a === void 0 ? void 0 : _a.removeChangeFlagInfo(this._defineChangeFlag);
    }
    _getOrCreateCacheEntry(context) {
        let pd = context.passData || null;
        let arr = this._passCache.get(pd);
        if (!arr) {
            arr = [null, null, null, null];
            this._passCache.set(pd, arr);
        }
        let gamma = !context._destRT || context._destRT._textures[0].gammaCorrection != 1;
        let idx = (context.invertY ? 2 : 0) | (gamma ? 1 : 0);
        let entry = arr[idx];
        if (!entry) {
            entry = new Pass2DCacheInfo();
            if (pd === null || pd === void 0 ? void 0 : pd._defineDatas) {
                pd._defineDatas.addChangeFlagInfo(entry.passDefineFlag);
                entry.trackedPassData = pd;
            }
            arr[idx] = entry;
        }
        return entry;
    }
    _compileShader(context) {
        var passes = this._subShader._passes;
        let entry = this._curCacheEntry;
        let renderCount = 0;
        for (var j = 0, m = passes.length; j < m; j++) {
            var pass = passes[j];
            if (pass.pipelineMode !== context.pipelineMode)
                continue;
            var comDef = WebGLRenderElement2D._compileDefine;
            if (this._globalShaderData) {
                this._globalShaderData._defineDatas.cloneTo(comDef);
            }
            else {
                context._globalConfigShaderData.cloneTo(comDef);
            }
            if (context.passData) {
                comDef.addDefineDatas(context.passData._defineDatas);
            }
            let returnGamma = !(context._destRT) || ((context._destRT)._textures[0].gammaCorrection != 1);
            if (returnGamma) {
                comDef.add(ShaderDefines2D.GAMMASPACE);
            }
            else {
                comDef.remove(ShaderDefines2D.GAMMASPACE);
            }
            if (context.invertY) {
                comDef.add(ShaderDefines2D.INVERTY);
            }
            else {
                comDef.remove(ShaderDefines2D.INVERTY);
            }
            if (this._value2DShaderData) {
                comDef.addDefineDatas(this._value2DShaderData.getDefineData());
                pass.nodeCommonMap = this.nodeCommonMap;
            }
            if (this._materialShaderData)
                comDef.addDefineDatas(this._materialShaderData._defineDatas);
            var shaderIns = pass.withCompile(comDef, true);
            entry.shaderInss[renderCount++] = shaderIns;
        }
        entry.shaderInss.length = renderCount;
    }
    _prepare(context) {
        this.globalShaderData = this.owner && this.owner._globalShaderData;
        let entry = this._getOrCreateCacheEntry(context);
        this._curCacheEntry = entry;
        let cfx = entry.cacheFlagX, cfy = entry.cacheFlagY;
        let dcf = this._defineChangeFlag;
        let pdf = entry.passDefineFlag;
        if (entry.cachedDirtyVersion !== this._dirtyVersion
            || dcf.x > cfx || (dcf.x === cfx && dcf.y > cfy)
            || pdf.x > cfx || (pdf.x === cfx && pdf.y > cfy)) {
            entry.cachedDirtyVersion = this._dirtyVersion;
            entry.cacheFlagX = Stat.loopCount;
            entry.cacheFlagY = WebGLEngine.instance._framePassCount;
            this._compileShader(context);
        }
    }
    _render(context) {
        context._prevTypeKey = -1;
        context._prevTextureKey = -1;
        context._prevClip = null;
        context._prevShaderIns = null;
        let inss = this._curCacheEntry.shaderInss;
        let count = inss.length;
        if (count === 1) {
            this.renderByShaderInstance(inss[0], context);
        }
        else {
            for (let j = 0; j < count; j++) {
                this.renderByShaderInstance(inss[j], context);
            }
        }
    }
    _uploadGlobalAndPass(shader, context) {
        this._globalShaderData && shader.uploadUniforms(shader._cameraUniformParamsMap, this._globalShaderData, true);
        context.passData && shader.uploadUniforms(shader._sceneUniformParamsMap, context.passData, true);
    }
    renderByShaderInstance(shader, context) {
        if (!shader.complete || !this.geometry)
            return;
        shader.bind();
        this._uploadGlobalAndPass(shader, context);
        this._value2DShaderData && shader.uploadUniforms(shader._sprite2DUniformParamsMap, this._value2DShaderData, true);
        this._materialShaderData && shader.uploadUniforms(shader._materialUniformParamsMap, this._materialShaderData, true);
        if (this.renderStateIsBySprite || !this._materialShaderData) {
            shader.uploadRenderStateBlendDepth(this._value2DShaderData);
            shader.uploadRenderStateFrontFace(this._value2DShaderData, false, context.invertY);
        }
        else {
            shader.uploadRenderStateBlendDepth(this._materialShaderData);
            shader.uploadRenderStateFrontFace(this._materialShaderData, false, context.invertY);
        }
        WebGLEngine.instance.getDrawContext().drawGeometryElement(this.geometry);
    }
    destroy() {
        this._unregisterDefineFlag(this._globalShaderData);
        this._unregisterDefineFlag(this._materialShaderData);
        this._unregisterDefineFlag(this._value2DShaderData);
        if (this._passCache) {
            this._passCache.forEach(arr => {
                var _a, _b;
                for (let i = 0; i < 4; i++) {
                    let entry = arr[i];
                    if (entry) {
                        (_b = (_a = entry.trackedPassData) === null || _a === void 0 ? void 0 : _a._defineDatas) === null || _b === void 0 ? void 0 : _b.removeChangeFlagInfo(entry.passDefineFlag);
                    }
                }
            });
        }
        this._globalShaderData = null;
        this._materialShaderData = null;
        this._value2DShaderData = null;
        this._passCache = null;
        this._curCacheEntry = null;
    }
}
WebGLRenderElement2D._compileDefine = new WebDefineDatas();
